import _ from 'underscore';
import { edmCampaign } from '@bingads-webui/edm-campaign';

import { showErrorBanner, logSingleResponse } from '../utils';
import { StandardBudgetType, LifetimeStandardBudgetType } from '../constants';

export const updateCampaignBudget = ({
  cid, aid, campaignId, attrs,
}) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .Campaigns.$withKey(campaignId)
  .bulkUpdate({
    Id: campaignId,
    ...attrs,
  });

export const updateCampaignBudgetMany = ({
  cid, aid, campaignIdList, attrsList,
}) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .Campaigns.$withKey()
  .bulkUpdateMany(
    campaignIdList,
    attrsList
  );

export const updateSharedBudget = ({
  cid, aid, budgetId, attrs,
}) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .Budgets.$withKey(budgetId)
  .bulkUpdate({
    Id: budgetId,
    ...attrs,
  });

export const updateSharedBudgetMany = ({
  cid, aid, budgetIdList, attrsList,
}) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .Budgets.$withKey()
  .bulkUpdateMany(
    budgetIdList,
    attrsList
  );

// generateUpdateCampaignBudgetPromises is to generate update campaign budget promises for many accounts.
export const generateUpdateCampaignBudgetPromises = ({ cid, campaigns, type }) => {
  const campaignGroups = _.groupBy(campaigns, item => item.AccountId);
  const accountIds = _.keys(campaignGroups);
  return accountIds.map((aid) => {
    const itemGroup = campaignGroups[aid];
    const attrsList = _.map(itemGroup, i => ({ Budget: { Amount: parseFloat(i.SuggestedBudget), Type: type } }));
    return updateCampaignBudgetMany({
      cid,
      aid,
      campaignIdList: _.map(itemGroup, i => i.CampaignId),
      attrsList,
    });
  });
};

// generateUpdateSharedBudgetManyPromises is to generate update shared budget promises for many accounts.
export const generateUpdateSharedBudgetManyPromises = ({ cid, campaigns }) => {
  const campaignGroups = _.groupBy(campaigns, item => item.AccountId);
  const accountIds = _.keys(campaignGroups);
  return accountIds.map((aid) => {
    const uniqItems = _.uniq(campaignGroups[aid], i => i.BudgetId);
    const attrsList = _.map(uniqItems, i => ({ Amount: parseFloat(i.SuggestedBudget) }));
    return updateSharedBudgetMany({
      cid,
      aid,
      budgetIdList: _.map(uniqItems, i => i.BudgetId),
      attrsList,
    });
  });
};

export const onSaveBudgetUpdate = ({
  budget,
  i18n,
  cid,
  budgetUpdateItem,
  budgetUpdateItem: { AccountId, CampaignId, BudgetId, DailyBudget },
  setIsSaving = _.noop,
  successCallback = _.noop,
  activity,
  // below options are mainly for testing purpose
  updateSharedBudgetFunc = updateSharedBudget,
  updateCampaignBudgetFunc = updateCampaignBudget,
  showErrorBannerFunc = showErrorBanner,
  logSingleResponseFunc = logSingleResponse,
}) => {
  (activity && activity.trace(JSON.stringify({ ...budgetUpdateItem, newBudget: budget }), 'updateBudget-before'));
  let updateBudgetPromise;
  if (BudgetId > 0) {
    const attrs = { Amount: parseFloat(budget) };
    updateBudgetPromise = updateSharedBudgetFunc({
      cid, aid: AccountId, budgetId: BudgetId, attrs,
    });
  } else {
    const attrs = { Budget: { Amount: parseFloat(budget), Type: DailyBudget !== null ? StandardBudgetType : LifetimeStandardBudgetType } };
    updateBudgetPromise = updateCampaignBudgetFunc({
      cid, aid: AccountId, campaignId: CampaignId, attrs,
    });
  }

  setIsSaving(true);

  return updateBudgetPromise.then((response) => {
    successCallback();
    (activity && logSingleResponseFunc({
      activity,
      result: response,
      api: 'updateBudget',
      sourceItems: [budgetUpdateItem],
    }));
  }).catch((response) => {
    showErrorBannerFunc({ response, i18n });
    (activity && logSingleResponseFunc({
      activity,
      result: response,
      api: 'updateBudget',
      sourceItems: [budgetUpdateItem],
    }));
  }).finally(() => setIsSaving(false));
};
